import { GetServerSideProps } from 'next'
import React from 'react'
import { Article as ArticleSchema, WithContext } from 'schema-dts'

import { Breadcrumbs } from '@Components/Breadcrumbs'
import { CMSPage } from '@Components/CMS/Page'
import { Page, PageType } from '@Generated/graphql'
import { initializeApolloFromContext } from '@Globals/Apollo/Server'
import { useBlockComponents } from '@Globals/CMS/useBlockComponents'
import { useLocale } from '@Globals/Locale'
import { PageHOC } from '@Globals/Page/Client'
import { boostFromContext, handleRedirect, pageProps } from '@Globals/Page/Server'
import { PageProps } from '@Globals/Page/Types'
import { getSinglePost } from '@Queries/cms/SinglePost.query'

interface ArticleProps extends PageProps {
  item: Page
}

type Params = {
  id: string
  slug: string | string[]
}

export const getServerSideProps: GetServerSideProps<ArticleProps, Params> = async (context) => {
  const { params } = context
  if (!params?.id) {
    return {
      notFound: true,
    }
  }

  const url = new URL(context.req.url || '', 'http://localhost')

  const apolloClient = initializeApolloFromContext(context)
  const post = await getSinglePost(
    apolloClient,
    PageType.Article,
    url.pathname,
    params.id,
    boostFromContext(context),
    context?.req?.thSplitTests,
  )
  const { page } = post.data

  if (!page) {
    return {
      notFound: true,
    }
  }

  if (page?.type === 'redirect_permanent' || page?.type === 'redirect_temporary') {
    return handleRedirect(context, page.url, page.type)
  }

  return {
    props: {
      ...await pageProps(
        context,
        {
          id: params.id,
          type: page.type,
        },
        page,
        ['cart', 'common', 'cms', 'footer', 'header', 'sidebar', 'validation', 'shipping', 'products'],
      ),
      item: page as Page,
    },
  }
}

const Article = PageHOC<ArticleProps>(({ item }) => {
  const components = useBlockComponents(item.blocks || [])
  const locale = useLocale()

  const schema: WithContext<ArticleSchema> | undefined = (item?.seo?.description || item?.seo?.title)
    ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      author: {
        '@type': 'Organization',
        name: 'Trendhim',
      },
      description: item.seo?.description || undefined,
      headline: item.seo?.title || undefined,
      inLanguage: locale || undefined,
      publisher: {
        '@type': 'Organization',
        name: 'Trendhim',
      },
    }
    : undefined

  return (
    <CMSPage>
      <Breadcrumbs withMaxWidth />
      {schema && <script dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, '\\u003c') }} type="application/ld+json" />}
      {components}
    </CMSPage>
  )
})

export default Article
